/**
 * Echo Hunt -- the cliché blacklist, as data (P9 follow-up, §5.5 + §10.3 + §13.3).
 *
 * A blacklist entry is a phrase that must never appear in a bullet (`CheckBullet`'s `cliche` violation).
 * It is consumed by the fake generator, the shared reshape, the corpus index pass and the weaver, so an
 * edit here takes effect on the next model call with no other plumbing.
 *
 * ## Why an editor exists at all
 *
 * The plan puts blacklist GROWTH in P13 (propose a tell phrase at the reveal → the user confirms → it
 * becomes an entry), but a list that can only be appended to is not a list you can trust: a phrase
 * entered by accident, or one that is simply badly worded, would otherwise be permanent. So the editing
 * primitives (add / rename / remove) and the "phrases waiting to be promoted" reader live here now, and
 * P13 only adds the proposal/permission flow on top.
 *
 * ## Why entries are deduped case-insensitively but stored as typed
 *
 * `CheckBullet` matches case-insensitively, so `Neatly resolved` and `neatly resolved` behave
 * identically -- keeping both would be two entries doing one job, and removing only one of them would
 * look like the removal failed. The typed casing is preserved for display, because a phrase the user
 * wrote should read back the way they wrote it.
 *
 * PURE module: no React, no `store`, no DB imports (see README.md).
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §5.5, §10.3, §13.3, §16.2 P13.
 */
import {ClicheBlacklistEntry, ClicheBlacklistEntrySource} from "./Types.js";

// hygiene
// ==========

/** Phrases shorter than this are flagged in the editor. A one-word entry matches any bullet containing
 * that word, and a rejected bullet is re-generated or dropped (§16.2 P7) -- so a careless one-word entry
 * quietly costs round supply. A warning rather than a block: the user may genuinely mean it. */
export const MIN_SAFE_CLICHE_WORDS = 2;

/** Trimmed, with internal whitespace collapsed. `CheckBullet` already treats runs of whitespace as one
 * space, so storing the collapsed form keeps what is displayed equal to what is matched. */
export function NormalizeClichePhrase(phrase: string|n): string {
	return (phrase ?? "").trim().replace(/\s+/g, " ");
}

/** The dedup/compare key. Case-insensitive because the matcher is. */
export function ClichePhraseKey(phrase: string|n): string {
	return NormalizeClichePhrase(phrase).toLowerCase();
}

/** True when a phrase is short enough to be worth a warning in the editor (see
 * `MIN_SAFE_CLICHE_WORDS`). Empty phrases are not "risky", they are simply invalid. */
export function IsRiskyClichePhrase(phrase: string|n): boolean {
	const normalized = NormalizeClichePhrase(phrase);
	if (normalized.length == 0) return false;
	const words = normalized.split(" ").filter(a=>a.length > 0);
	return words.length < MIN_SAFE_CLICHE_WORDS;
}

// edits
// ==========

export type BlacklistEditResult =
	| {ok: true, entries: ClicheBlacklistEntry[]}
	| {ok: false, error: string};

/** Appends a phrase, or reports why it cannot be added. Pure: the caller writes the returned list. */
export function AddClicheEntry(entries: ClicheBlacklistEntry[]|n, phrase: string|n, source: ClicheBlacklistEntrySource = "manual"): BlacklistEditResult {
	const list = entries ?? [];
	const normalized = NormalizeClichePhrase(phrase);
	if (normalized.length == 0) return {ok: false, error: "The phrase is empty."};

	const key = ClichePhraseKey(normalized);
	if (list.some(entry=>ClichePhraseKey(entry.phrase) == key)) {
		return {ok: false, error: `"${normalized}" is already on the list.`};
	}

	return {ok: true, entries: [...list, {phrase: normalized, source, hits: 0}]};
}

/** Rewrites one entry's phrase, preserving its `source` and `hits` (§13.3 keeps the hit counts). */
export function RenameClicheEntry(entries: ClicheBlacklistEntry[]|n, oldPhrase: string|n, newPhrase: string|n): BlacklistEditResult {
	const list = entries ?? [];
	const oldKey = ClichePhraseKey(oldPhrase);
	const index = list.findIndex(entry=>ClichePhraseKey(entry.phrase) == oldKey);
	if (index < 0) return {ok: false, error: "That entry is no longer on the list."};

	const normalized = NormalizeClichePhrase(newPhrase);
	if (normalized.length == 0) return {ok: false, error: "The phrase is empty."};

	const newKey = ClichePhraseKey(normalized);
	if (list.some((entry, i)=>i != index && ClichePhraseKey(entry.phrase) == newKey)) {
		return {ok: false, error: `"${normalized}" is already on the list.`};
	}
	if (newKey == oldKey) {
		// no-op rather than an error: re-saving an unchanged phrase is a normal thing to do
		return {ok: true, entries: list};
	}

	const updated = [...list];
	updated[index] = {...list[index], phrase: normalized};
	return {ok: true, entries: updated};
}

/** Removes one entry. Removing a phrase that is not present is a no-op -- the caller's intent
 * ("that entry should not be there") is already satisfied. */
export function RemoveClicheEntry(entries: ClicheBlacklistEntry[]|n, phrase: string|n): ClicheBlacklistEntry[] {
	const key = ClichePhraseKey(phrase);
	return (entries ?? []).filter(entry=>ClichePhraseKey(entry.phrase) != key);
}

/** Removes the entries at the given phrase keys, in one pass. Used by the editor's "remove selected". */
export function RemoveClicheEntries(entries: ClicheBlacklistEntry[]|n, phrases: string[]|n): ClicheBlacklistEntry[] {
	const keys = new Set((phrases ?? []).map(ClichePhraseKey));
	return (entries ?? []).filter(entry=>!keys.has(ClichePhraseKey(entry.phrase)));
}

// tell phrases waiting to be promoted (§10.3 -> §13.3)
// ==========

/** One tell phrase the user typed at a reveal (§10.3), with enough context to promote it. */
export type TellPhraseCandidate = {
	phrase: string;
	/** the round it was reported on, so the editor can show when it was said */
	roundId: string;
	slotId: string;
	/** the round's `createdAt`, for ordering */
	reportedAt: number;
};

/** The minimal round shape this needs; `EchoRound` is structurally assignable. */
export type TellPhraseRoundSource = {
	id: string;
	createdAt: number;
	answers?: {slotId: string, tellPhrase?: string|n}[]|n;
};

/** Tell phrases the user has typed at reveals but that are NOT yet blacklist entries (§13.3 "grow the
 * blacklist from your reveal-time flags").
 *
 * This is what makes the editor useful rather than a blank form: the phrases a user reports are already
 * collected on each round (P9), and §13.3 wants them promoted -- but only with confirmation, because a
 * reported tell is a description of what they noticed, not necessarily a phrase a fake should be banned
 * from using. So they are surfaced as CANDIDATES here and P13 adds the one-click confirm.
 *
 * Deduped case-insensitively: the same tell tends to be reported more than once, and the newest
 * occurrence wins (it is the one the user most recently cared about).
 */
export function CollectTellPhrases(rounds: TellPhraseRoundSource[]|n, blacklist: ClicheBlacklistEntry[]|n): TellPhraseCandidate[] {
	const blocked = new Set((blacklist ?? []).map(entry=>ClichePhraseKey(entry.phrase)));
	const byKey = new Map<string, TellPhraseCandidate>();

	for (const round of rounds ?? []) {
		for (const answer of round.answers ?? []) {
			const phrase = NormalizeClichePhrase(answer.tellPhrase);
			if (phrase.length == 0) continue;

			const key = ClichePhraseKey(phrase);
			if (blocked.has(key)) continue;

			const existing = byKey.get(key);
			if (existing != null && existing.reportedAt >= round.createdAt) continue;
			byKey.set(key, {phrase, roundId: round.id, slotId: answer.slotId, reportedAt: round.createdAt});
		}
	}

	return [...byKey.values()].sort((a, b)=>b.reportedAt - a.reportedAt);
}
